import { type ReactNode, useCallback, useContext } from "react";
import { Link, type LinkProps, useHistory, useRouteMatch } from "react-router-dom";

import { Hotkey } from "@btnet/core-components/Hotkey";
import {
    type IHotkeyWithPopoverConfig,
    type THotkeyCommands,
} from "@btnet/core-components/Hotkey/hotkey.types";
import { getHotkeyProps } from "@btnet/core-components/Hotkey/hotkey.utility";
import {
    type ComponentName,
    type EventName,
    type IAnalyticsExtraInfoValue,
    useAnalyticsClickHandler,
} from "@btnet/utilities-analytics";

import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isRouteLocationState } from "utilities/flatRoutes/routes";
import { routes } from "utilities/routes";
import { scrollToElementById } from "utilities/scroll/scroll";
import { formatRouteForNewTab } from "utilities/url/url";

import "./BTLink.less";

/**
 * If using linkType 'scroll', make sure that your href is the same as the id of the element you want to scroll to
 */
interface IBTLinkProps extends Omit<LinkProps, "target" | "rel"> {
    children?: ReactNode;
    shouldOpenNewTab?: boolean;
    /**
     * @default true
     */
    isUnderline?: boolean;
    "data-testid"?: string;
    linkType?: "link" | "list" | "menuList" | "scroll" | "tel" | "sms" | "email";
    isDisabled?: boolean;
    hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;

    /**
     * Automatically handles links to SPA pages and adds /app in front of the route if needed
     */
    useAutoSPARouting?: boolean;

    /**
     * When supplied, clicking the link emits a Sawmill v3 analytics event with
     * `eventType: "click"` and this `eventName`. Omit to skip v3 tracking.
     */
    analyticsEventName?: EventName;
    /**
     * Optional `componentName` for the v3 analytics event. Only honored when
     * `analyticsEventName` is also supplied.
     */
    analyticsComponentName?: ComponentName;
    /**
     * Optional `extraInfo` for the v3 analytics event (e.g. `{ bill_id }`).
     * Only honored when `analyticsEventName` is also supplied.
     *
     * For values the call site knows and the surrounding context cannot — a
     * grid cell knows which row was clicked, the page-level provider does not.
     */
    analyticsExtraInfo?: Record<string, IAnalyticsExtraInfoValue>;
    /**
     * Optional `jobId` for the v3 analytics event. Only honored when
     * `analyticsEventName` is also supplied.
     *
     * This is the event's **top-level** `jobId`, which the warehouse attributes
     * on — not the same thing as `extraInfo.selected_job_id`, which the enricher
     * fills with the job picker's current selection. On an "All Jobs" list
     * those are different jobs, so a call site that knows the real one should
     * pass it here. Left unset, `buildEvent` falls back to the context's jobId.
     */
    analyticsJobId?: number;
}

function BTLinkInternal({
    analyticsComponentName,
    analyticsEventName,
    analyticsExtraInfo,
    analyticsJobId,
    children,
    className = "",
    hotkey,
    href,
    isDisabled = false,
    isUnderline = true,
    linkType,
    onClick,
    shouldOpenNewTab = false,
    tracking,
    useAutoSPARouting,
    ...restProps
}: IBTLinkProps & ITrackingProp) {
    const history = useHistory();
    const fireAnalyticsClick = useAnalyticsClickHandler({
        eventName: analyticsEventName,
        componentType: "link",
        componentName: analyticsComponentName,
        // Spread rather than passed as undefined: both are optional keys, and
        // exactOptionalPropertyTypes distinguishes absent from explicitly-undefined.
        ...(analyticsExtraInfo !== undefined && { extraInfo: analyticsExtraInfo }),
        ...(analyticsJobId !== undefined && { jobId: analyticsJobId }),
    });

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            if (onClick) {
                onClick(event);
            }
            if (linkType === "scroll") {
                scrollToElementById(href!);
            }

            tracking?.trackEvent({ event: "LinkClick" });
            fireAnalyticsClick();
        },
        [fireAnalyticsClick, href, linkType, onClick, tracking],
    );

    if (isDisabled) {
        return <>{children}</>;
    }

    let linkListStyling = "";
    switch (linkType) {
        case "link":
            break;

        case "list":
            linkListStyling = "linkListItem";
            break;

        case "menuList":
            linkListStyling = "menuListItem";
            break;
    }
    const underlineClass = isUnderline ? "link-underline" : "link-nounderline";
    const classNameWithDefault = `${className} BTLink ${underlineClass} ${linkListStyling}`;

    let modifiedTarget: "_blank" | undefined;
    let modifiedRel: "noopener noreferrer" | undefined;

    if (shouldOpenNewTab) {
        modifiedTarget = "_blank";
        modifiedRel = "noopener noreferrer";
    }

    if (linkType === "tel" || linkType === "sms") {
        const { to, ...propsWithoutTo } = restProps;
        return (
            <a className={classNameWithDefault} href={href} {...propsWithoutTo}>
                {children}
            </a>
        );
    }

    const link =
        restProps.to.toString() === "" ? (
            <a
                {...restProps}
                className={classNameWithDefault}
                target={modifiedTarget}
                onClick={handleClick}
                rel={modifiedRel}
                href={href}
            >
                {children}
            </a>
        ) : (
            <Link
                {...restProps}
                className={classNameWithDefault}
                target={modifiedTarget}
                onClick={handleClick}
                rel={modifiedRel}
                href={href}
            >
                {children}
            </Link>
        );

    if (hotkey) {
        return (
            <Hotkey
                {...getHotkeyProps(hotkey)}
                disabled={isDisabled}
                onCommand={() => {
                    if (onClick) {
                        onClick({} as React.MouseEvent<HTMLAnchorElement, MouseEvent>);
                    }
                    if (shouldOpenNewTab) {
                        window.open(
                            formatRouteForNewTab(restProps.to.toString()),
                            "_blank",
                            "noopener noreferrer",
                        );
                    } else if (restProps.to) {
                        history.push(restProps.to.toString());
                    } else if (href) {
                        window.location.assign(href);
                    }
                }}
            >
                {link}
            </Hotkey>
        );
    }
    return link;
}

export const BTLink = track((props) => ({
    element: "Link",
    uniqueId: props["data-testid"],
}))(function ({ to, href, useAutoSPARouting, ...otherProps }: IBTLinkProps) {
    const spaInfoContext = useContext(SpaInfoContext);

    // temporary until all warnings are gone - just to be safe
    let toValue = to;
    if (!isRouteLocationState(toValue) && toValue?.toString().toLowerCase().startsWith("/app")) {
        toValue = toValue.toString().replace("/app", "");
        console.warn("Do not pass /app as part of your route. Pass the part after /app");
    }

    /**
     *
     * Update 'to' field to dynamically add base route automatically
     * Remove once we are fully SPA
     */
    const updateTo = () => {
        const isSPA = spaInfoContext?.isSpa;
        if (isRouteLocationState(to) && isSPA) {
            return to;
        }
        if (!useAutoSPARouting && !isRouteLocationState(to)) {
            return toValue;
        }

        if (isSPA) {
            return toValue.toString();
        } else {
            return "";
        }
    };

    /**
     *
     * Redirect 'to' fields to use href links when we are not in SPA
     * Remove once we are fully SPA
     */ const updateHref = () => {
        const isSPA = spaInfoContext?.isSpa;
        if (isRouteLocationState(to) && !isSPA) {
            return routes.appBase + to.pathname;
        }
        if (!useAutoSPARouting) {
            return href;
        }

        if (isSPA) {
            return "";
        } else {
            if (toValue.toString() === "") {
                return href;
            } else {
                if (toValue.toString().includes(".aspx")) {
                    console.warn(
                        "Do not pass webform urls through BTLink's to prop, use BTExternalLink or href instead",
                    );
                    return toValue.toString();
                } else {
                    return `${routes.appBase}${toValue.toString()}`;
                }
            }
        }
    };

    return <BTLinkInternal to={updateTo()} href={updateHref()} {...otherProps} />;
});

/**
 * Use this link whenever navigating to a route nested within your page
 * @param props Whatever props you would normally pass to BTLink
 */
export const BTLinkRelative: React.FunctionComponent<IBTLinkProps> = track((props) => ({
    element: "Link",
    uniqueId: props["data-testid"],
}))((props: IBTLinkProps & ITrackingProp) => {
    const { to, ...rest } = props;
    const match = useRouteMatch();

    if (typeof to === "object") {
        return <BTLinkInternal {...rest} to={{ ...to, pathname: `${match.url}${to.pathname}` }} />;
    }
    return <BTLinkInternal {...rest} to={`${match.url}${to}`} />;
});
